import { jsx } from "react/jsx-runtime";
import { useRef, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";
const DESMOS_SCRIPT_ID = "desmos-api-script";
const DESMOS_API_URL = "https://www.desmos.com/api/v1.5/calculator.js?apiKey=dcb31709b452b1cf9dc26972add0fda6";
function loadDesmosScript() {
  return new Promise((resolve, reject) => {
    if (window.Desmos) {
      resolve();
      return;
    }
    const existingScript = document.getElementById(DESMOS_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve());
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Failed to load Desmos API"))
      );
      return;
    }
    const script = document.createElement("script");
    script.id = DESMOS_SCRIPT_ID;
    script.src = DESMOS_API_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Desmos API"));
    document.head.appendChild(script);
  });
}
function processVarsIntoExpressions(expressionList, vars) {
  if (!vars) return expressionList;
  const regexObj = {};
  Object.keys(vars).forEach((key) => {
    if (key.startsWith("_")) {
      let varValue = vars[key];
      let varKey = key.replace("_", "");
      if (Array.isArray(varValue)) {
        varValue = `[${varValue.join(",")}]`;
      }
      if (varKey.length > 1) {
        varKey = `${varKey.slice(0, 1)}_{${varKey.slice(1)}}`;
      }
      regexObj[varKey] = String(varValue);
    }
  });
  const usedKeys = [];
  const newList = [];
  expressionList.forEach((item) => {
    const newItem = { ...item };
    if (newItem.latex && newItem.latex.includes("=")) {
      let latexKey = newItem.latex.split("=")[0].trim();
      latexKey = latexKey.replace("\\", "").trim();
      const varValue = regexObj[latexKey];
      if (varValue !== void 0) {
        newItem.latex = `${latexKey}=${varValue}`;
        newItem.isChanged = true;
        usedKeys.push(latexKey);
      }
    }
    newList.push(newItem);
  });
  Object.keys(regexObj).filter((k) => !usedKeys.includes(k)).forEach((key, index) => {
    newList.push({
      type: "expression",
      id: `extra_${index}`,
      latex: `${key}=${regexObj[key]}`
    });
  });
  return newList;
}
function DesmosGraph({
  state,
  vars,
  onChange,
  className,
  showExpressions = false
}) {
  const { t } = useTranslation("quiz");
  const containerRef = useRef(null);
  const calculatorRef = useRef(null);
  const [viewport, setViewport] = useState("landscape");
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const handleChange = useCallback(
    (calculator) => {
      if (!onChange) return;
      const expressions = calculator.getExpressions();
      const data = {};
      const regex = /^([a-zA-Z0-9_{}]+)[\s\\]*=[\s\\]*([-+]?[0-9.]+)$/;
      expressions.forEach((e) => {
        const latex = e.latex;
        if (!latex) return;
        const match = regex.exec(latex);
        if (match) {
          data[match[1].replace(/[_{}]/g, "")] = match[2];
        } else {
          const analysis = calculator.expressionAnalysis[e.id];
          if (analysis == null ? void 0 : analysis.evaluation) {
            const varName = latex.split("=")[0].replace(/[_{}\\]/g, "").trim();
            data[varName] = String(analysis.evaluation.value);
          }
        }
      });
      onChange(data);
    },
    [onChange]
  );
  const initCalculator = useCallback(async () => {
    if (!containerRef.current || !state) return;
    try {
      await loadDesmosScript();
      if (!window.Desmos) {
        setError("Desmos API not available");
        return;
      }
      if (calculatorRef.current) {
        calculatorRef.current.destroy();
        calculatorRef.current = null;
      }
      containerRef.current.innerHTML = "";
      let parsedState;
      try {
        parsedState = JSON.parse(state);
      } catch {
        setError("Invalid Desmos state JSON");
        return;
      }
      let expressionList = [];
      if (parsedState.expressions) {
        if (Array.isArray(parsedState.expressions)) {
          expressionList = parsedState.expressions;
        } else if (parsedState.expressions.list) {
          expressionList = parsedState.expressions.list;
        }
      }
      parsedState.expressions = { list: [] };
      const options = {
        keypad: false,
        graphpaper: true,
        expressions: showExpressions,
        settingsMenu: false,
        zoomButtons: false,
        expressionsTopbar: false,
        pointsOfInterest: false,
        trace: false,
        border: false,
        lockViewport: false,
        showGrid: true,
        showXAxis: true,
        showYAxis: true,
        fontSize: 24
      };
      if (parsedState._options) {
        Object.assign(options, parsedState._options);
        if (parsedState._options.viewport) {
          setViewport(parsedState._options.viewport);
        }
      }
      const calculator = window.Desmos.GraphingCalculator(
        containerRef.current,
        options
      );
      calculatorRef.current = calculator;
      const processedList = processVarsIntoExpressions(expressionList, vars);
      parsedState.expressions = { list: processedList };
      calculator.setState(parsedState);
      if (onChange) {
        handleChange(calculator);
        calculator.observeEvent("change", () => {
          handleChange(calculator);
        });
        calculator.observe("expressionAnalysis", () => {
          handleChange(calculator);
        });
      }
      setIsLoaded(true);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to initialize Desmos");
    }
  }, [state, vars, onChange, showExpressions, handleChange]);
  useEffect(() => {
    initCalculator();
    return () => {
      if (calculatorRef.current) {
        calculatorRef.current.destroy();
        calculatorRef.current = null;
      }
    };
  }, [initCalculator]);
  if (error) {
    return /* @__PURE__ */ jsx("div", { className: cn("flex items-center justify-center p-4 text-red-500", className), children: /* @__PURE__ */ jsx("span", { children: t("shared.cannotLoadGraph", { error }) }) });
  }
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref: containerRef,
      className: cn(
        "desmos-graph",
        viewport === "portrait" ? "aspect-3/4" : "aspect-4/3",
        "w-full max-w-[600px] min-h-[300px]",
        !isLoaded && "bg-gray-100 animate-pulse",
        className
      )
    }
  );
}
export {
  DesmosGraph
};
//# sourceMappingURL=desmos-graph.js.map
